import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import SpacedRepetition from '../components/SRS/SpacedRepetition';
import * as roadmapService from '../services/roadmapService';
import { isRoadmapUserCookbook } from '../services/roadmapService';
import { loadRoadmapSnippetStatsByIds } from '../services/profileAchievementStatsService';
import {
  isSnippetMastered,
  isSnippetStarted
} from '../components/Roadmap/RoadmapList';
import { formatCatalogTrackDisplayTitle } from './roadmapDisplay';
import { buildOrderedNodeGraph } from './roadmapGraph';
import { getRoadmapTrackIdFromUserDeckData } from './roadmapSubscription';

/**
 * @typedef {object} RolePathTrackRow
 * @property {string} trackId
 * @property {string} trackTitle
 * @property {string|null} language
 * @property {number} snippetTotal
 * @property {number} snippetStarted
 * @property {number} snippetMastered
 * @property {boolean} isComplete
 */

/**
 * @typedef {object} RolePathOverview
 * @property {RolePathTrackRow[]} tracks
 * @property {number} snippetsTotal
 * @property {number} snippetsStarted
 * @property {number} snippetsMastered
 * @property {number} tracksCompleted
 * @property {number} tracksTotal
 * @property {string|null} resumeTrackId
 * @property {string|null} resumeTrackTitle
 * @property {string|null} resumeLanguage
 * @property {string|null} resumeNodeTitle
 * @property {string|null} resumeNodeId
 * @property {boolean} allComplete
 */

/**
 * @typedef {object} RolePathResume
 * @property {string|null} trackId
 * @property {string|null} trackTitle
 * @property {string|null} language
 * @property {string|null} nodeTitle
 * @property {string|null} nodeId
 * @property {number} tracksCompleted
 * @property {number} tracksTotal
 * @property {boolean} allComplete
 */

/** @type {Map<string, RolePathOverview>} */
const overviewCache = new Map();

/** @type {Map<string, Promise<RolePathOverview|null>>} */
const overviewInflight = new Map();

function overviewCacheKey(userId, roleId) {
  return `${userId || ''}:${roleId || ''}`;
}

export function clearRolePathOverviewCache(userId, roleId) {
  if (userId && roleId) {
    overviewCache.delete(overviewCacheKey(userId, roleId));
    overviewInflight.delete(overviewCacheKey(userId, roleId));
    return;
  }
  overviewCache.clear();
  overviewInflight.clear();
}

function computeSnippetCounts(nodes, snippetStatsById) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return { total: 0, started: 0, mastered: 0 };
  }

  const seen = new Set();
  let total = 0;
  let started = 0;
  let mastered = 0;

  for (const node of nodes) {
    const ids = node?.snippetIds;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      total += 1;
      const stats = snippetStatsById?.[id];
      if (isSnippetMastered(stats)) mastered += 1;
      if (isSnippetStarted(stats)) started += 1;
    }
  }

  return { total, started, mastered };
}

function normalizeSnippetStatsResult(result) {
  if (!result || typeof result !== 'object') return {};
  if (result.statsBySnippetId && typeof result.statsBySnippetId === 'object') {
    return result.statsBySnippetId;
  }
  return result;
}

function resolveTrackMeta(step, catalogTracks) {
  const byId = new Map((catalogTracks || []).map((t) => [t.trackId, t]));
  const track = byId.get(step.trackId);
  if (track) return track;
  return { trackId: step.trackId, title: step.trackId, language: null };
}

function findFirstIncompleteNode(ordered, statsById) {
  return ordered.find((node) => {
    const ids = node.snippetIds || [];
    if (ids.length === 0) return true;
    return ids.some((id) => !isSnippetMastered(statsById[id]));
  });
}

function resolveResumeNode(ordered, statsById, preferredNodeId) {
  if (preferredNodeId) {
    const preferred = ordered.find((node) => node.id === preferredNodeId);
    if (preferred) {
      const ids = preferred.snippetIds || [];
      const hasIncomplete =
        ids.length === 0 || ids.some((id) => !isSnippetMastered(statsById[id]));
      if (hasIncomplete) {
        return { nodeId: preferred.id, nodeTitle: preferred.title || null };
      }
    }
  }
  const firstIncomplete = findFirstIncompleteNode(ordered, statsById);
  return {
    nodeId: firstIncomplete?.id || null,
    nodeTitle: firstIncomplete?.title || null
  };
}

/**
 * Most recently reviewed roadmap track within a role path (SRS `lastReviewed`).
 *
 * @param {string|null|undefined} userId
 * @param {Set<string>} rolePathTrackIds
 * @returns {Promise<{ trackId: string, nodeId: string|null, lastReviewedMs: number }|null>}
 */
async function findMostRecentlyStudiedRolePathTrack(userId, rolePathTrackIds) {
  if (!userId || !rolePathTrackIds?.size) return null;

  let decksSnap;
  let progressRows;
  try {
    [decksSnap, progressRows] = await Promise.all([
      getDocs(collection(db, `users/${userId}/decks`)),
      new SpacedRepetition(userId).fetchAllCardProgressRows()
    ]);
  } catch {
    return null;
  }

  const deckInfo = new Map();
  decksSnap.forEach((deckDoc) => {
    const data = deckDoc.data();
    const deckWithId = { id: deckDoc.id, ...data };
    if (!isRoadmapUserCookbook(deckWithId)) return;
    const trackId = getRoadmapTrackIdFromUserDeckData(data);
    if (!trackId || !rolePathTrackIds.has(trackId)) return;
    deckInfo.set(deckDoc.id, {
      trackId,
      nodeId: data.roadmapNodeId ?? null
    });
  });

  let best = null;
  for (const row of progressRows || []) {
    if (!row?.deckId || !row?.lastReviewed) continue;
    const reviewedAt = new Date(row.lastReviewed).getTime();
    if (!Number.isFinite(reviewedAt)) continue;
    const info = deckInfo.get(row.deckId);
    if (!info) continue;
    if (!best || reviewedAt > best.lastReviewedMs) {
      best = {
        trackId: info.trackId,
        nodeId: info.nodeId,
        lastReviewedMs: reviewedAt
      };
    }
  }

  return best;
}

async function loadRolePathOverviewUncached(userId, role, catalogTracks) {
  if (!role?.tracks?.length) return null;

  const orderedSteps = role.tracks
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((step) => step?.trackId);

  if (orderedSteps.length === 0) return null;

  const nodeResults = await Promise.all(
    orderedSteps.map(async (step) => {
      try {
        const nodes = await roadmapService.loadTrackNodes(step.trackId);
        const ordered = buildOrderedNodeGraph(nodes);
        return { step, ordered };
      } catch {
        return { step, ordered: [] };
      }
    })
  );

  const trackDataList = nodeResults
    .filter(({ ordered }) => ordered.length > 0)
    .map(({ step, ordered }) => {
      const snippetIds = [];
      ordered.forEach((node) => {
        (node.snippetIds || []).forEach((id) => {
          if (id) snippetIds.push(id);
        });
      });
      return { step, ordered, snippetIds };
    });

  if (trackDataList.length === 0) return null;

  const allSnippetIds = [...new Set(trackDataList.flatMap((row) => row.snippetIds))];
  const rolePathTrackIds = new Set(trackDataList.map(({ step }) => step.trackId));

  let statsById = {};
  let recentStudy = null;
  if (userId) {
    const [statsResult, recentStudyResult] = await Promise.all([
      allSnippetIds.length > 0
        ? loadRoadmapSnippetStatsByIds(userId, allSnippetIds).catch(() => ({}))
        : Promise.resolve({}),
      findMostRecentlyStudiedRolePathTrack(userId, rolePathTrackIds)
    ]);
    statsById = normalizeSnippetStatsResult(statsResult);
    recentStudy = recentStudyResult;
  }

  /** @type {RolePathTrackRow[]} */
  const tracks = [];
  let snippetsTotal = 0;
  let snippetsStarted = 0;
  let snippetsMastered = 0;
  let tracksCompleted = 0;
  let tracksTotal = 0;

  let resumeTrackId = null;
  let resumeTrackTitle = null;
  let resumeLanguage = null;
  let resumeNodeTitle = null;
  let resumeNodeId = null;

  for (const { step, ordered } of trackDataList) {
    const track = resolveTrackMeta(step, catalogTracks);
    tracksTotal += 1;

    const counts = computeSnippetCounts(ordered, statsById);
    snippetsTotal += counts.total;
    snippetsStarted += counts.started;
    snippetsMastered += counts.mastered;

    const isComplete = counts.total > 0 && counts.mastered === counts.total;
    if (isComplete) {
      tracksCompleted += 1;
    } else if (!resumeTrackId) {
      const firstIncompleteNode = findFirstIncompleteNode(ordered, statsById);
      resumeTrackId = step.trackId;
      resumeTrackTitle = formatCatalogTrackDisplayTitle(track.language, track.title, track);
      resumeLanguage = track.language || null;
      resumeNodeTitle = firstIncompleteNode?.title || null;
      resumeNodeId = firstIncompleteNode?.id || null;
    }

    tracks.push({
      trackId: step.trackId,
      trackTitle: formatCatalogTrackDisplayTitle(track.language, track.title, track),
      language: track.language || null,
      snippetTotal: counts.total,
      snippetStarted: counts.started,
      snippetMastered: counts.mastered,
      isComplete
    });
  }

  const allComplete = tracksCompleted === tracksTotal;

  if (!allComplete && recentStudy?.trackId) {
    const recentTrackRow = tracks.find((row) => row.trackId === recentStudy.trackId);
    const recentTrackData = trackDataList.find(({ step }) => step.trackId === recentStudy.trackId);
    if (recentTrackRow && !recentTrackRow.isComplete && recentTrackData) {
      const track = resolveTrackMeta(recentTrackData.step, catalogTracks);
      const { nodeId, nodeTitle } = resolveResumeNode(
        recentTrackData.ordered,
        statsById,
        recentStudy.nodeId
      );
      resumeTrackId = recentStudy.trackId;
      resumeTrackTitle = formatCatalogTrackDisplayTitle(track.language, track.title, track);
      resumeLanguage = track.language || null;
      resumeNodeTitle = nodeTitle;
      resumeNodeId = nodeId;
    }
  }

  return {
    tracks,
    snippetsTotal,
    snippetsStarted,
    snippetsMastered,
    tracksCompleted,
    tracksTotal,
    resumeTrackId: allComplete ? null : resumeTrackId,
    resumeTrackTitle: allComplete ? null : resumeTrackTitle,
    resumeLanguage: allComplete ? null : resumeLanguage,
    resumeNodeTitle: allComplete ? null : resumeNodeTitle,
    resumeNodeId: allComplete ? null : resumeNodeId,
    allComplete
  };
}

/**
 * Load role-path progress: per-track snippet stats, resume point, and totals.
 * Parallel track-node reads + one batched snippet-stats pass; cached per user/role.
 *
 * @param {string|null|undefined} userId
 * @param {{ roleId?: string, tracks?: Array<{ trackId: string, order?: number }> }|null|undefined} role
 * @param {Array<{ trackId: string, title?: string, language?: string }>} catalogTracks
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<RolePathOverview|null>}
 */
export async function computeRolePathOverview(userId, role, catalogTracks, options = {}) {
  const { force = false } = options;
  const roleId = role?.roleId;
  if (!roleId) return loadRolePathOverviewUncached(userId, role, catalogTracks);

  const cacheKey = overviewCacheKey(userId, roleId);
  if (!force && overviewCache.has(cacheKey)) {
    return overviewCache.get(cacheKey) || null;
  }

  if (!force && overviewInflight.has(cacheKey)) {
    return overviewInflight.get(cacheKey);
  }

  const promise = loadRolePathOverviewUncached(userId, role, catalogTracks)
    .then((overview) => {
      if (overview) overviewCache.set(cacheKey, overview);
      else overviewCache.delete(cacheKey);
      overviewInflight.delete(cacheKey);
      return overview;
    })
    .catch((err) => {
      overviewInflight.delete(cacheKey);
      throw err;
    });

  overviewInflight.set(cacheKey, promise);
  return promise;
}

/**
 * Find the first track in a role path that is not snippet-mastered, plus progress counts.
 *
 * @param {string|null|undefined} userId
 * @param {{ roleId?: string, tracks?: Array<{ trackId: string, order?: number }> }|null|undefined} role
 * @param {Array<{ trackId: string, title?: string, language?: string }>} catalogTracks
 * @returns {Promise<RolePathResume|null>}
 */
export async function computeRolePathResume(userId, role, catalogTracks) {
  const overview = await computeRolePathOverview(userId, role, catalogTracks);
  if (!overview) return null;

  return {
    trackId: overview.resumeTrackId,
    trackTitle: overview.resumeTrackTitle,
    language: overview.resumeLanguage,
    nodeTitle: overview.resumeNodeTitle,
    nodeId: overview.resumeNodeId,
    tracksCompleted: overview.tracksCompleted,
    tracksTotal: overview.tracksTotal,
    allComplete: overview.allComplete
  };
}

/**
 * @param {RolePathResume|null|undefined} resume
 * @returns {string}
 */
export function rolePathResumeSubtitle(resume) {
  if (!resume) return 'Open your role path in Learn';
  if (resume.allComplete) {
    return resume.tracksTotal > 0
      ? `All ${resume.tracksTotal} tracks mastered — review anytime`
      : 'Open your role path in Learn';
  }
  if (resume.nodeTitle && resume.trackTitle) {
    return `Next: ${resume.trackTitle} · ${resume.nodeTitle}`;
  }
  if (resume.trackTitle) {
    return `Next: ${resume.trackTitle}`;
  }
  if (resume.tracksTotal > 0) {
    return `${resume.tracksCompleted} of ${resume.tracksTotal} tracks complete`;
  }
  return 'Open your role path in Learn';
}

